import PublicLayout from "@/components/layouts/bootstrap/PublicLayout";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "@/lib/axios";
import { useToasts } from "@/hooks/toasts";
import { isValidPhoneNumber } from "libphonenumber-js";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { Input } from "@/components/forms/Input";
import { PhoneNumberInput } from "@/components/forms/PhoneNumberInput";
import Seo from "@/components/Seo";

export default function Contact()
{
  const { addToast } = useToasts();
  const [loading, setLoading] = useState(false);

  const contactValidationSchema = Yup.object().shape({
    name: Yup.string().required("Please add your name"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Please add your email address"),
    mobile: Yup.string()
      .required("Phone number is required")
      .test(
        "is-valid-format",
        "Phone number is invalid or too long",
        (value) => isValidPhoneNumber(value || "")
      ),
    message: Yup.string().required("Please explain how we can help."),
  });

  const searchParams = useSearchParams();
  const from = searchParams.get("from");

  const contactForm = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobile: "",
      subject: "NA",
      message: "",
    },
    validationSchema: contactValidationSchema,
    onSubmit: async (values, { resetForm }) =>
    {
      try
      {
        setLoading(true);
        Object.keys(values).forEach((key) =>
        {
          if (typeof values[key] === "string")
          {
            values[key] = values[key].trim();
          }
        });
        const formData = new FormData();
        Object.entries(values).forEach(([key, val]) => formData.append(key, val));
        if (from === 'demo')
        {
          await axios.post("/api/support-request", formData);
          addToast("success", "Message sent successfully!");
        } else
        {
          await axios.post("/api/contact-us", formData);
          addToast("success", "Message sent successfully!");
        }
        addToast("success", "Message sent successfully!.");
        resetForm();
      } catch (error)
      {
        console.error("Error submitting contact form:", error);
        addToast("error", "Something went wrong. Please try again.");
      } finally
      {
        setLoading(false);
      }
    },
  });

  return (
    <>
      <Seo
        title="Contact Instant Polls | Get Help With Live Polling Software"
        description="Talk to our team to learn how real-time polling can improve meetings, events, and audience engagement."
        canonical="https://instantpolls.online/contact"
      />
      <section className="hero-reseller d-flex align-items-center text-white responsiveZindex">
        <div className="container hero-inner">
          <div className="hero-copy">
            <h1>Contact Us</h1>
            <p>
              For more information about Instant Polls, you can reach us anytime using
              the contact form.
            </p>
            <div>
              {from === "demo" ? (
                <p>
                  <i className="bi bi-envelope-fill text-primary me-3 fs-4"></i>
                  Email: reseller@instantpolls.online
                </p>
              ) : (
                <p>
                  <i className="bi bi-envelope-fill text-primary me-3 fs-4"></i>
                  Email: office@instantpolls.online
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 patner responsiveZindex">
        <Container>
          <div className="cta-band rounded-4 p-4 ">
            <Row className="align-items-center">
              <Col lg={8} className="mx-auto">
                <div className="contact-card">
                  {from === "demo" ? (
                    <h3 className="mb-3">Apply to Become a Partner</h3>
                  ) : (
                    <h3 className="mb-3">Get In Touch</h3>
                  )}
                  <p className="mb-4">
                    Please call our office or use the form below to email us your
                    message. We will endeavour to reply to your inquiry as soon as
                    possible during business hours.
                  </p>

                  <Form onSubmit={contactForm.handleSubmit} noValidate>
                    <Form.Group className="mb-3">
                      <label htmlFor="name" className="form-label">
                        Full name
                      </label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className={`form-control`}
                        form={contactForm}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <label htmlFor="email" className="form-label">
                        Email address
                      </label>
                      <Input
                        type="email"
                        id="email"
                        maxLength={150}
                        className={`form-control`}
                        form={contactForm}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <label htmlFor="mobile" className="form-label">
                        Phone number
                      </label>
                      <PhoneNumberInput
                        id="mobile"
                        name="mobile"
                        className="form-control p-0"
                        form={contactForm}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <label htmlFor="message" className="form-label">
                        Message
                      </label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        id="message"
                        name="message"
                        className={`form-control ${contactForm.touched.message && contactForm.errors.message
                          ? "is-invalid"
                          : ""
                          }`}
                        maxLength={200}
                        value={contactForm.values.message}
                        onChange={contactForm.handleChange}
                        onBlur={contactForm.handleBlur}
                      />
                      {contactForm.touched.message &&
                        contactForm.errors.message && (
                          <div className="invalid-feedback">
                            {contactForm.errors.message}
                          </div>
                        )}
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100"
                      disabled={loading}
                    >
                      {loading ? "Sending..." : "Submit Request"}
                    </Button>
                  </Form>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>
    </>
  );
}

Contact.getLayout = (page) => <PublicLayout>{page}</PublicLayout>;
